import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FileOutput, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { CardContent, CardIcon } from '@/components/ui/card'
import { DataExportsApi, type DataExport } from '@/services/networks/dataExportsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { AppShell } from '../../components/AppShell'
import { BulkActionBar } from '../../components/BulkActionBar'
import { DataExportTable } from '../../components/DataExportTable'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { StatusFilterButton } from '../../components/StatusFilter'
import type { ListSort } from '@/services/core/paging'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

export function DataExportsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>(
    'filters.dataExports.status',
    'ACTIVE'
  )
  const [search, setSearch] = useUrlSearch()
  const [selection, setSelection] = useState<DataExport[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)

  const { data, isLoading } = useQuery({
    queryKey: ['data-exports', 'page', search, statusFilter, page, pageSize, sort],
    queryFn: () => DataExportsApi.getPage({ page, pageSize, search, status: statusFilter, sort }),
    staleTime: Infinity,
  })
  const dataExports = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((e) => DataExportsApi.update(e.id, { name: e.name, objectState: state }))
      )
      queryClient.invalidateQueries({ queryKey: ['data-exports'] })
      toast.success(t('common.done'), {
        description: t(
          state === 'ACTIVE' ? 'dataExports.doneDetail_activate' : 'dataExports.doneDetail_deactivate',
          { count: selection.length }
        ),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('dataExports.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('dataExports.title')}</h1>
            <p>{t('dataExports.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button onClick={() => navigate(`/admin/data-exports/${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}?new=true`)}>
              <Plus className="size-4" /> {t('dataExports.newDataExport')}
            </Button>
          </div>
        </div>

        <ListCard>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={FileOutput} />
                {t('dataExports.title')}
              </>
            }
            count={totalCount}
            actions={
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => refilter(() => setPageSize(size))}
              />
            }
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                />
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              {
                label: t('common.activate'),
                icon: 'pi pi-check-circle',
                severity: 'success',
                onClick: () => bulkSetStatus('ACTIVE'),
              },
              {
                label: t('common.deactivate'),
                icon: 'pi pi-ban',
                severity: 'danger',
                onClick: () => bulkSetStatus('INACTIVE'),
              },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <DataExportTable
              dataExports={dataExports}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={t('dataExports.noDataExportsFound')}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('dataExports.dataExportCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
